import React, { ChangeEvent, useMemo } from 'react';
import styled from 'styled-components';

import Button from 'kingpin/atoms/Button';
import Icon from 'kingpin/atoms/Icon';
import Typography from 'kingpin/atoms/Typography';
import {
  GadgetFormInner,
  GadgetFormInnerContent,
  GadgetFormWrapper,
} from 'components/V5GadgetForm/V5GadgetForm';
import Inputs from 'components/Inputs';
import Row from 'components/Common/Row';
import ReportSelector from 'components/ConfigureDashboardGadget/ReportSelector';
import Tooltip from 'components/Tooltip';
import { Section } from 'components/MyAccount/Profile/styles';
import DragAndDropList from 'components/DragAndDropList';
import DragHandle from 'components/DragAndDropList/DragHandle';

const ItemWrapper = styled.div`
  margin-bottom: 8px;
  background-color: var(--color-bg-muted);
  padding: 12px;
`;

const InputWrapper = styled.div`
  margin-bottom: 8px;
`;

const Label = ({ children }: { children: string }) => (
  <div style={{ marginBottom: 4 }}>
    <Typography.Body type="Label">{children}</Typography.Body>
  </div>
);

const DIRECTION_OPTIONS: DropdownOption[] = [
  { label: 'Ascending', key: 'asc', onSelected: () => {} },
  { label: 'Descending', key: 'desc', onSelected: () => {} },
];

const TRENDING_INTERVAL_OPTIONS: Array<{
  label: string;
  key: FleetOps.NonAutoInterval;
}> = [
  { label: 'Hour', key: 'hour' },
  { label: 'Day', key: 'day' },
  { label: 'Week', key: 'week' },
  { label: 'Month', key: 'month' },
  { label: 'Quarter', key: 'quarter' },
  { label: 'Year', key: 'year' },
];

const EntityInformationTableForm = ({
  name,
  onNameChanged,
  description,
  onDescriptionChanged,
  datasetOptions,
  fieldsByDatasetId,
  rows,
  datasetSorts,
  onRowDatasetChanged,
  onRowFieldChanged,
  onRowMetricChanged,
  onRemoveRow,
  onRowReportDrillDownChanged,
  onRowBoardDrillDownChanged,
  onDatasetSortFieldChanged,
  onDatasetSortDirectionChanged,
  onAddFieldRow,
  onAddMetricRow,
  onRowTrendingChanged,
  onRowsReordered,
  columnsPerRow,
  onColumnsPerRowChanged,
  renderMode,
  onRenderModeChanged,
  isReport = false,
}: {
  name: string;
  onNameChanged: (event: ChangeEvent<HTMLInputElement>) => void;
  description: string;
  onDescriptionChanged: (event: ChangeEvent<HTMLInputElement>) => void;
  datasetOptions: DropdownOption[];
  fieldsByDatasetId: Record<string, Array<{ field: string; label: string }>>;
  rows: VisualisationDefinitions.EntityInformationTableRow[];
  datasetSorts: Record<
    string,
    VisualisationDefinitions.EntityInformationTableDatasetSort
  >;
  onRowDatasetChanged: (key: string, datasetId: string) => void;
  onRowFieldChanged: (key: string, field: string) => void;
  onRowMetricChanged: (key: string, metricId: string | undefined) => void;
  onRemoveRow: (key: string) => void;
  onRowReportDrillDownChanged: (key: string, id: string | undefined) => void;
  onRowBoardDrillDownChanged: (key: string, id: string | undefined) => void;
  onDatasetSortFieldChanged: (datasetId: string, sortByField: string) => void;
  onDatasetSortDirectionChanged: (
    datasetId: string,
    sortByDirection: SortBy,
  ) => void;
  onAddFieldRow: () => void;
  onAddMetricRow: () => void;
  onRowTrendingChanged: (
    key: string,
    trending:
      | VisualisationDefinitions.EntityInformationTableMetricRowTrending
      | undefined,
  ) => void;
  onRowsReordered: (
    newOrder: VisualisationDefinitions.EntityInformationTableRow[],
  ) => void;
  columnsPerRow: 1 | 2;
  onColumnsPerRowChanged: (value: 1 | 2) => void;
  renderMode: 'grid' | 'cards';
  onRenderModeChanged: (value: 'grid' | 'cards') => void;
  isReport?: boolean;
}) => {
  const activeDatasets = useMemo(() => {
    const seen = new Set<string>();
    const result: Array<{ datasetId: string; label: string }> = [];
    rows.forEach((r) => {
      if (r.type === 'field' && r.datasetId && !seen.has(r.datasetId)) {
        seen.add(r.datasetId);
        const option = datasetOptions.find((o) => o.key === r.datasetId);
        result.push({
          datasetId: r.datasetId,
          label: option?.label ?? r.datasetId,
        });
      }
    });
    return result;
  }, [rows, datasetOptions]);

  return (
    <GadgetFormWrapper>
      <GadgetFormInner isGadgetBuilder={false}>
        <GadgetFormInnerContent isGadgetBuilder={false}>
          <Section>
            <Typography.Body type="Label">Name</Typography.Body>
            <Inputs.TextInput
              placeholder="Name"
              value={name}
              onChange={onNameChanged}
            />
          </Section>
          <Section>
            <Typography.Body type="Label">Description</Typography.Body>
            <Inputs.TextInput
              placeholder="Description"
              value={description}
              onChange={onDescriptionChanged}
            />
          </Section>
          <Section>
            <Typography.Body type="Label">Render mode</Typography.Body>
            <Inputs.Dropdown
              placeholder="Select render mode"
              selectedLabel={renderMode === 'cards' ? 'Cards' : 'Grid'}
              options={[
                {
                  label: 'Cards',
                  key: 'cards',
                  isSelected: renderMode === 'cards',
                  onSelected: () => onRenderModeChanged('cards'),
                },
                {
                  label: 'Grid',
                  key: 'grid',
                  isSelected: renderMode === 'grid',
                  onSelected: () => onRenderModeChanged('grid'),
                },
              ]}
            />
          </Section>
          {renderMode === 'cards' && (
            <Section>
              <Typography.Body type="Label">Columns per row</Typography.Body>
              <Row centerAlign style={{ gap: 8 }}>
                <Inputs.Checkbox
                  isChecked={columnsPerRow === 1}
                  onToggled={() => onColumnsPerRowChanged(1)}
                />
                <Typography.Body type="Body 12">1</Typography.Body>
              </Row>
              <Row centerAlign style={{ gap: 8 }}>
                <Inputs.Checkbox
                  isChecked={columnsPerRow === 2}
                  onToggled={() => onColumnsPerRowChanged(2)}
                />
                <Typography.Body type="Body 12">2</Typography.Body>
              </Row>
            </Section>
          )}
          <Section>
            <Typography.Body type="Label">Rows</Typography.Body>
            <DragAndDropList
              items={rows}
              onOrderChanged={onRowsReordered}
              droppableId="entity-information-table-rows"
              renderItem={(row) => {
                if (row.type === 'field') {
                  const rowDatasetOptions: DropdownOption[] = datasetOptions
                    .map((o) => ({
                      ...o,
                      onSelected: () =>
                        onRowDatasetChanged(row.key, o.key ?? ''),
                    }))
                    .sort((a, b) => a.label.localeCompare(b.label));
                  const availableFields =
                    fieldsByDatasetId[row.datasetId] ?? [];
                  const fieldDropdownOptions: DropdownOption[] = availableFields
                    .map((f) => ({
                      label: f.label,
                      key: f.field,
                      onSelected: () => onRowFieldChanged(row.key, f.field),
                    }))
                    .sort((a, b) => a.label.localeCompare(b.label));
                  const selectedDatasetOption = datasetOptions.find(
                    (o) => o.key === row.datasetId,
                  );
                  const selectedField = availableFields.find(
                    (f) => f.field === row.field,
                  );
                  return (
                    <ItemWrapper>
                      <Row spaceBetween centerAlign style={{ marginBottom: 8 }}>
                        <Row centerAlign style={{ gap: 4 }}>
                          <DragHandle noMargin />
                          <Typography.Body
                            type="Label"
                            color="var(--color-text-tertiary)"
                          >
                            Document Field
                          </Typography.Body>
                        </Row>
                        <Button
                          onClick={() => onRemoveRow(row.key)}
                          icon="cross"
                          type="Secondary"
                          size="Small"
                        />
                      </Row>
                      <InputWrapper>
                        <Label>Dataset</Label>
                        <Inputs.Dropdown
                          placeholder="Select a dataset"
                          selectedLabel={
                            selectedDatasetOption
                              ? selectedDatasetOption.label
                              : undefined
                          }
                          options={rowDatasetOptions}
                          isSearchEnabled
                        />
                      </InputWrapper>
                      <InputWrapper>
                        <Label>Field</Label>
                        <Inputs.Dropdown
                          placeholder="Select a field"
                          selectedLabel={
                            selectedField ? selectedField.label : undefined
                          }
                          options={fieldDropdownOptions}
                          isSearchEnabled
                        />
                      </InputWrapper>
                      {!isReport && (
                        <ReportSelector
                          label="Drill down"
                          reportId={row.reportDrillDownId}
                          setReportId={(id) =>
                            onRowReportDrillDownChanged(
                              row.key,
                              id as string | undefined,
                            )
                          }
                          boardId={row.boardDrillDownId}
                          setBoardId={(id) =>
                            onRowBoardDrillDownChanged(
                              row.key,
                              id as string | undefined,
                            )
                          }
                          isBlockInput
                        />
                      )}
                    </ItemWrapper>
                  );
                }

                const trendIntervalOptions: DropdownOption[] =
                  TRENDING_INTERVAL_OPTIONS.map((o) => ({
                    label: o.label,
                    key: o.key,
                    isSelected: row.trending?.interval === o.key,
                    onSelected: () =>
                      onRowTrendingChanged(row.key, {
                        interval: o.key,
                        numberOfIntervals: row.trending?.numberOfIntervals ?? 6,
                      }),
                  }));
                const selectedIntervalOption = TRENDING_INTERVAL_OPTIONS.find(
                  (o) => o.key === row.trending?.interval,
                );

                return (
                  <ItemWrapper>
                    <Row spaceBetween centerAlign style={{ marginBottom: 8 }}>
                      <Row centerAlign style={{ gap: 4 }}>
                        <DragHandle noMargin />
                        <Typography.Body
                          type="Label"
                          color="var(--color-text-tertiary)"
                        >
                          Metric
                        </Typography.Body>
                      </Row>
                      <Button
                        onClick={() => onRemoveRow(row.key)}
                        icon="cross"
                        type="Secondary"
                        size="Small"
                      />
                    </Row>
                    <InputWrapper>
                      <Label>Metric</Label>
                      <Inputs.MetricPicker
                        metricId={row.metricId || undefined}
                        setMetricId={(id: string | undefined) =>
                          onRowMetricChanged(row.key, id)
                        }
                        noSpecials
                      />
                    </InputWrapper>
                    <InputWrapper>
                      <Row centerAlign style={{ gap: 8 }}>
                        <Inputs.Checkbox
                          isChecked={!!row.trending}
                          onToggled={(checked) =>
                            onRowTrendingChanged(
                              row.key,
                              checked
                                ? { interval: 'month', numberOfIntervals: 6 }
                                : undefined,
                            )
                          }
                        />
                        <Typography.Body type="Body 12">
                          Trending
                        </Typography.Body>
                      </Row>
                    </InputWrapper>
                    {row.trending && (
                      <InputWrapper>
                        <Row centerAlign style={{ gap: 8 }}>
                          <div style={{ flex: 1 }}>
                            <Label>Interval</Label>
                            <Inputs.Dropdown
                              placeholder="Select interval"
                              selectedLabel={
                                selectedIntervalOption
                                  ? selectedIntervalOption.label
                                  : undefined
                              }
                              options={trendIntervalOptions}
                            />
                          </div>
                          <div style={{ flex: 1 }}>
                            <Label>Number of intervals</Label>
                            <Inputs.Number
                              value={row.trending.numberOfIntervals}
                              setValue={(n) =>
                                onRowTrendingChanged(row.key, {
                                  ...row.trending!,
                                  numberOfIntervals: n ?? 1,
                                })
                              }
                              placeholder="6"
                            />
                          </div>
                        </Row>
                      </InputWrapper>
                    )}
                    {!isReport && (
                      <ReportSelector
                        label="Drill down"
                        reportId={row.reportDrillDownId}
                        setReportId={(id) =>
                          onRowReportDrillDownChanged(
                            row.key,
                            id as string | undefined,
                          )
                        }
                        boardId={row.boardDrillDownId}
                        setBoardId={(id) =>
                          onRowBoardDrillDownChanged(
                            row.key,
                            id as string | undefined,
                          )
                        }
                        metricId={row.metricId || undefined}
                        isBlockInput
                      />
                    )}
                  </ItemWrapper>
                );
              }}
            />
            <Row centerAlign style={{ gap: 8 }}>
              <Button
                onClick={onAddFieldRow}
                label="+ Document Field"
                type="Secondary"
                size="Small"
              />
              <Button
                onClick={onAddMetricRow}
                label="+ Metric"
                type="Secondary"
                size="Small"
              />
            </Row>
          </Section>
          {activeDatasets.length > 0 && (
            <Section>
              <Row centerAlign style={{ gap: 4, marginBottom: 4 }}>
                <Typography.Body type="Label">Dataset sort</Typography.Body>
                <Tooltip
                  content={
                    'Each dataset fetch returns the first document that matches the active filters and entity context. The sort order determines which document is selected — for example, sorting by date descending gives you the most recent record.'
                  }
                >
                  <Icon icon="info" color={'var(--color-grey-3)'} />
                </Tooltip>
              </Row>
              {activeDatasets.map(({ datasetId, label }) => {
                const sort = datasetSorts[datasetId] ?? {};
                const availableFields = fieldsByDatasetId[datasetId] ?? [];
                const sortFieldOptions: DropdownOption[] = availableFields.map(
                  (f) => ({
                    label: f.label,
                    key: f.field,
                    onSelected: () =>
                      onDatasetSortFieldChanged(datasetId, f.field),
                  }),
                );
                const directionOptions: DropdownOption[] =
                  DIRECTION_OPTIONS.map((o) => ({
                    ...o,
                    onSelected: () =>
                      onDatasetSortDirectionChanged(datasetId, o.key as SortBy),
                  }));
                const selectedSortField = availableFields.find(
                  (f) => f.field === sort.sortByField,
                );
                return (
                  <ItemWrapper key={datasetId}>
                    <div style={{ marginBottom: 8 }}>
                      <Typography.Body
                        type="Label"
                        color="var(--color-text-tertiary)"
                      >
                        {label}
                      </Typography.Body>
                    </div>
                    <Row centerAlign style={{ gap: 8 }}>
                      <div style={{ flex: 1 }}>
                        <Label>Sort field</Label>
                        <Inputs.Dropdown
                          placeholder="date"
                          selectedLabel={
                            selectedSortField
                              ? selectedSortField.label
                              : undefined
                          }
                          options={sortFieldOptions}
                        />
                      </div>
                      <div style={{ flex: 1 }}>
                        <Label>Direction</Label>
                        <Inputs.Dropdown
                          placeholder="Descending"
                          selectedLabel={
                            sort.sortByDirection === 'asc'
                              ? 'Ascending'
                              : sort.sortByDirection === 'desc'
                                ? 'Descending'
                                : undefined
                          }
                          options={directionOptions}
                        />
                      </div>
                    </Row>
                  </ItemWrapper>
                );
              })}
            </Section>
          )}
        </GadgetFormInnerContent>
      </GadgetFormInner>
    </GadgetFormWrapper>
  );
};

export default EntityInformationTableForm;
